// Look-control tuning, persisted between sessions.

const STORAGE_KEY = 'ttg:look-settings';

export const SENSITIVITY = {
  min: 0.15,
  max: 2.5,
  step: 0.05,
  default: 0.55,
};

export const SMOOTHING = {
  min: 0,
  max: 0.9,
  step: 0.05,
  default: 0.35,
};

export const DEFAULT_LOOK_SETTINGS = {
  sensitivity: SENSITIVITY.default,
  smoothing: SMOOTHING.default,
  invertY: false,
};

function clampRange(value, range, fallback) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(range.max, Math.max(range.min, number));
}

/** Coerces anything (stored JSON, URL params, user input) into valid settings. */
export function normalizeLookSettings(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};

  return {
    sensitivity: clampRange(source.sensitivity, SENSITIVITY, SENSITIVITY.default),
    smoothing: clampRange(source.smoothing, SMOOTHING, SMOOTHING.default),
    invertY: source.invertY === true,
  };
}

export function loadLookSettings(storage) {
  try {
    const stored = (storage ?? globalThis.localStorage)?.getItem(STORAGE_KEY);
    return normalizeLookSettings(stored ? JSON.parse(stored) : null);
  } catch {
    // Private browsing, disabled storage, or corrupt JSON — fall back to defaults.
    return { ...DEFAULT_LOOK_SETTINGS };
  }
}

export function saveLookSettings(settings, storage) {
  const normalized = normalizeLookSettings(settings);
  try {
    (storage ?? globalThis.localStorage)?.setItem(STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // Persisting is best-effort; the session still gets the new values.
  }
  return normalized;
}

/**
 * Converts a raw pointer delta into radians.
 *
 * Browsers report `movementX` in CSS pixels, and the figure spikes badly on
 * Windows when the pointer re-centres or the tab regains focus. Clamping the
 * per-event delta is what stops the camera from snapping across the sky.
 */
export const MAX_POINTER_DELTA = 120;

export function pointerDeltaToRadians(delta, sensitivity) {
  const clamped = Math.max(-MAX_POINTER_DELTA, Math.min(MAX_POINTER_DELTA, delta));
  return clamped * 0.002 * sensitivity;
}
